// The application flasher. A native shell watches for the teamx:// protocol
// and flashes the taskbar button; the page triggers it by clicking a link.
//
// This used to be five near-copies across App, ChatBoxTabsWindow, MemberList,
// MemberListItem and UserHub, which had drifted apart. One copy means the
// preference has one gate.
import { isOn } from './notificationPreferences';

export const FRAME_ID = 'flashNotification';

// Long enough for the browser to have acted on the URL. Taking the frame out
// synchronously can cancel the launch before it happens.
export const FRAME_CLEANUP_MS = 1000;

export const removeTriggerFrame = () => {
    try {
        var existing = document.getElementById(FRAME_ID);
        if (existing && existing.parentNode) {
            existing.parentNode.removeChild(existing);
        }
    } catch (err) {
        // Nothing useful left to do, and no caller may hear about it.
    }
};

// The trigger navigates a throwaway iframe, NOT the page.
//
// This used to be an <a> appended to the body and clicked, which is a
// top-level navigation. On a machine with no registered teamx:// handler the
// browser starts that navigation, fires beforeunload on the document, and only
// then discovers it cannot satisfy the scheme and cancels - and App's
// beforeunload handler is a full logout: it drops the token, writes the
// profile offline, calls setChatDisplay(false) and closes every child window.
// So the page tore itself down on every incoming message: the chat pop-up
// closed (setChatDisplay unmounts <NewWindow>, whose componentWillUnmount
// calls window.close()), the user went offline, and because the navigation was
// then cancelled the SPA sat there tokenless until it was closed and reopened.
// Nothing threw and nothing appeared in the console but "Failed to launch",
// which arrives after the damage.
//
// With the handler installed the browser hands off out-of-process without
// navigating, so beforeunload never fires - which is why this only ever bit
// machines without the native shell.
//
// beforeunload belongs to the browsing context being navigated, so moving the
// navigation into an iframe keeps it away from the top-level document whatever
// the browser decides to do with the scheme. Nothing in here may throw into a
// caller either: every call site is a SignalR handler or a React lifecycle.
export const fireProtocolTrigger = (protocol, action, params) => {
    try {
        var body = document.getElementsByTagName('body')[0];
        if (!body) {
            return false;
        }

        var strParams = "";
        if (typeof params !== 'undefined') {
            Object.keys(params).forEach(function (key) {
                strParams += strParams !== "" ? '&' : '';
                strParams += key + '=' + encodeURI(params[key]);
            });
        }

        // A frame left by a previous message goes before another is added, so
        // a burst of messages cannot grow a stack of them.
        removeTriggerFrame();

        var frame = document.createElement('iframe');
        frame.id = FRAME_ID;
        frame.setAttribute('aria-hidden', 'true');
        frame.setAttribute('tabindex', '-1');
        frame.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden';
        frame.src = strParams.length > 0
            ? protocol + '://' + action + '?' + strParams
            : protocol + '://' + action;
        body.appendChild(frame);
        setTimeout(removeTriggerFrame, FRAME_CLEANUP_MS);
        return true;
    } catch (err) {
        removeTriggerFrame();
        return false;
    }
};

// Returns whether the trigger was fired, so callers dispatch setIconFlash only
// when it was - which keeps state.shouldTurnFlashOn honest. Mirrors
// showMessageNotification, which already reports back the same way.
//
// 'flashstop' is deliberately never gated: if the preference is switched off
// while the application is already flashing, the stop must still get through.
export const flashApp = (preferences, action) => {
    if (action === 'flashstart' && !isOn(preferences, 'notifications', 'flashApp')) {
        return false;
    }
    // The real answer, not an assumption that it worked. On a machine with no
    // teamx:// handler this is false, so state.shouldTurnFlashOn stays false
    // and nothing later tries to stop a flash that never started.
    return fireProtocolTrigger('teamx', action);
};

export default { fireProtocolTrigger, flashApp, removeTriggerFrame, FRAME_ID, FRAME_CLEANUP_MS };



// WEBPACK FOOTER //
// ./src/utils/flashNotification.js